import { useEffect } from 'react';
import { useEditMode } from '@/contexts/EditModeContext';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import { Button } from './ui/button';
import { ServiceForm } from './admin/ServiceForm';
import { BlogPostForm } from './admin/BlogPostForm';
import { CaseStudyForm } from './admin/CaseStudyForm';
import { TeamMemberForm } from './admin/TeamMemberForm';
import { TestimonialForm } from './admin/TestimonialForm';
import { useServices } from '@/hooks/useServices';
import { useBlogPosts } from '@/hooks/useBlogPosts';
import { useCaseStudies } from '@/hooks/useCaseStudies';
import { useTeamMembers } from '@/hooks/useTeamMembers';
import { useTestimonials } from '@/hooks/useTestimonials';

export const InlineFormPanel = () => {
  const { editPanelOpen, editPanelType, editPanelData, closeEditPanel } = useEditMode();
  const { createService, updateService } = useServices();
  const { createBlogPost, updateBlogPost } = useBlogPosts();
  const { createCaseStudy, updateCaseStudy } = useCaseStudies();
  const { createTeamMember, updateTeamMember } = useTeamMembers();
  const { createTestimonial, updateTestimonial } = useTestimonials();

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Cmd/Ctrl + S to save (handled by individual forms)
      // ESC to close
      if (e.key === 'Escape' && editPanelOpen) {
        closeEditPanel();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [editPanelOpen, closeEditPanel]);

  const handleSubmit = async (data: any) => {
    try {
      if (editPanelData) {
        // Update existing
        switch (editPanelType) {
          case 'service':
            await updateService.mutateAsync({ id: editPanelData.id, updates: data });
            break;
          case 'blog-post':
            await updateBlogPost.mutateAsync({ id: editPanelData.id, updates: data });
            break;
          case 'case-study':
            await updateCaseStudy.mutateAsync({ id: editPanelData.id, updates: data });
            break;
          case 'team-member':
            await updateTeamMember.mutateAsync({ id: editPanelData.id, updates: data });
            break;
          case 'testimonial':
            await updateTestimonial.mutateAsync({ id: editPanelData.id, updates: data });
            break;
        }
      } else {
        // Create new
        switch (editPanelType) {
          case 'service':
            await createService.mutateAsync(data);
            break;
          case 'blog-post':
            await createBlogPost.mutateAsync(data);
            break;
          case 'case-study':
            await createCaseStudy.mutateAsync(data);
            break;
          case 'team-member':
            await createTeamMember.mutateAsync(data);
            break;
          case 'testimonial':
            await createTestimonial.mutateAsync(data);
            break;
        }
      }
      closeEditPanel();
    } catch (error) {
      console.error('Failed to save:', error);
    }
  };

  const isLoading =
    createService.isPending ||
    updateService.isPending ||
    createBlogPost.isPending ||
    updateBlogPost.isPending ||
    createCaseStudy.isPending ||
    updateCaseStudy.isPending ||
    createTeamMember.isPending ||
    updateTeamMember.isPending ||
    createTestimonial.isPending ||
    updateTestimonial.isPending;

  const getTitle = () => {
    const action = editPanelData ? 'Rediger' : 'Tilføj Ny';
    switch (editPanelType) {
      case 'service':
        return `${action} Service`;
      case 'blog-post':
        return `${action} Blog Post`;
      case 'case-study':
        return `${action} Case Study`;
      case 'team-member':
        return `${action} Teammedlem`;
      case 'testimonial':
        return `${action} Testimonial`;
      default:
        return '';
    }
  };

  return (
    <AnimatePresence>
      {editPanelOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-background/80 backdrop-blur-sm z-40"
            onClick={closeEditPanel}
          />

          {/* Panel */}
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            className="fixed right-0 top-0 bottom-0 w-full max-w-2xl bg-background border-l shadow-2xl z-50 overflow-y-auto"
          >
            <div className="sticky top-0 bg-background border-b px-6 py-4 flex items-center justify-between z-10">
              <h2 className="text-2xl font-bold">{getTitle()}</h2>
              <Button variant="ghost" size="icon" onClick={closeEditPanel}>
                <X className="h-5 w-5" />
              </Button>
            </div>

            <div className="p-6">
              {editPanelType === 'service' && (
                <ServiceForm
                  open={true}
                  onOpenChange={closeEditPanel}
                  onSubmit={handleSubmit}
                  initialData={editPanelData}
                  isLoading={isLoading}
                />
              )}
              {editPanelType === 'blog-post' && (
                <BlogPostForm
                  open={true}
                  onOpenChange={closeEditPanel}
                  onSubmit={handleSubmit}
                  initialData={editPanelData}
                  isLoading={isLoading}
                />
              )}
              {editPanelType === 'case-study' && (
                <CaseStudyForm
                  open={true}
                  onOpenChange={closeEditPanel}
                  onSubmit={handleSubmit}
                  initialData={editPanelData}
                  isLoading={isLoading}
                />
              )}
              {editPanelType === 'team-member' && (
                <TeamMemberForm
                  open={true}
                  onOpenChange={closeEditPanel}
                  onSubmit={handleSubmit}
                  initialData={editPanelData}
                  isLoading={isLoading}
                />
              )}
              {editPanelType === 'testimonial' && (
                <TestimonialForm
                  open={true}
                  onOpenChange={closeEditPanel}
                  onSubmit={handleSubmit}
                  initialData={editPanelData}
                  isLoading={isLoading}
                />
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};
